import { describeApiError, type Room } from '@brush/api-client';
import { ErrorNotice, Spinner } from '@brush/ui';
import { History } from 'lucide-react';
import { boardsBehindCard } from '../activity/overview';
import type { RoomView } from '../nav';
import { useRoomActivity } from '../queries';

/**
 * The operator Overview's drift card (ADR 0012 step 23): the running boards that recalculate by hand or on a
 * schedule and have fallen behind the room's tournaments. Every rule deciding what it says is in
 * `activity/overview.ts`. It reads the same query as the Players section, so it costs no second request.
 * Not signal orange: a board behind is information with a way through to its screen, and the one control
 * that acts on it — Recalculate — is on that screen.
 */
export function BoardsBehindSection({ room, onNavigate }: { room: Room; onNavigate: (view: RoomView) => void }) {
  const query = useRoomActivity(room.id);

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="flex items-center gap-2 border-b border-ink-800/10 px-6 py-4">
        <History className="size-4 text-gold-500" />
        <h2 className="font-display font-semibold text-ink-950">Boards behind</h2>
      </header>
      <div className="p-6">
        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorNotice message={describeApiError(query.error)} />
        ) : (
          <BoardsBehind card={boardsBehindCard(query.data)} onNavigate={onNavigate} />
        )}
      </div>
    </section>
  );
}

function BoardsBehind({ card, onNavigate }: { card: ReturnType<typeof boardsBehindCard>; onNavigate: (view: RoomView) => void }) {
  return (
    <div className="space-y-3">
      {card.empty ? (
        <p className="text-sm text-ink-800">{card.empty}</p>
      ) : (
        <ul className="space-y-2">
          {card.rows.map((row) => (
            <li key={row.boardId} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-800/10 p-3">
              <div className="min-w-0">
                <div className="font-medium text-ink-950">
                  {row.name} <span className="text-xs font-normal text-ink-800/60">· {row.kind}</span>
                </div>
                <div className="text-xs text-ink-800">{row.says}</div>
                <div className="text-xs text-ink-800/60">
                  Last recalculated {new Date(row.lastRecalculatedAt).toLocaleString()}. {row.next}
                </div>
              </div>
              <button
                type="button"
                className="text-xs font-medium text-ink-800 underline-offset-2 hover:underline"
                onClick={() => onNavigate(row.kind === 'Rating' ? 'ratings' : 'leaderboards')}
              >
                Open {row.kind === 'Rating' ? 'ratings' : 'leaderboards'}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ink-800/60">{card.note}</p>
    </div>
  );
}
